import React, { useState, useCallback, useRef } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import EventTriggerNode from "./nodes/EventTriggerNode";
import SendEmailNode from "./nodes/SendEmailNode";
import SendWhatsAppNode from "./nodes/SendWhatsAppNode";
import AutomationDelayNode from "./nodes/AutomationDelayNode";
import AutomationConditionNode from "./nodes/AutomationConditionNode";
import AutomationHttpNode from "./nodes/AutomationHttpNode";
import AutomationNodePalette from "./AutomationNodePalette";
import AutomationNodeConfigPanel from "./AutomationNodeConfigPanel";

const nodeTypes = {
  eventTrigger: EventTriggerNode,
  sendEmail: SendEmailNode,
  sendWhatsApp: SendWhatsAppNode,
  delay: AutomationDelayNode,
  condition: AutomationConditionNode,
  httpRequest: AutomationHttpNode,
};

const defaultNodeData = {
  eventTrigger: { event: "" },
  sendEmail: { to: "", subject: "", htmlBody: "", body: "", templateKey: "" },
  sendWhatsApp: { to: "", message: "", sessionId: "" },
  delay: { amount: "5", unit: "m" },
  condition: { variable: "", operator: "equals", value: "" },
  httpRequest: { url: "", method: "POST", headers: {}, body: {} },
};

const MINIMAP_COLORS = {
  eventTrigger: "#059669",
  sendEmail: "#2563eb",
  sendWhatsApp: "#16a34a",
  delay: "#d97706",
  condition: "#7c3aed",
  httpRequest: "#0891b2",
};

let nodeIdCounter = 1;

export default function AutomationFlowCanvas({ initialNodes, initialEdges, onSave, sessions }) {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialNodes?.length ? initialNodes : [
      { id: "node-0", type: "eventTrigger", position: { x: 300, y: 50 }, data: { event: "" } },
    ]
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showPalette, setShowPalette] = useState(false);

  // Set starting counter from loaded nodes
  if (initialNodes?.length) {
    const maxId = Math.max(0, ...initialNodes.map((n) => {
      const m = n.id.match(/node-(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }));
    if (maxId >= nodeIdCounter) nodeIdCounter = maxId + 1;
  }

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            style: { stroke: "#00C897", strokeWidth: 2 },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const type = e.dataTransfer.getData("application/reactflow");
      if (!type || !reactFlowInstance) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      const newNode = {
        id: `node-${nodeIdCounter++}`,
        type,
        position,
        data: { ...defaultNodeData[type] },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, setNodes]
  );

  const onAddNode = useCallback(
    (type) => {
      const newNode = {
        id: `node-${nodeIdCounter++}`,
        type,
        position: { x: 300, y: 50 + nodes.length * 140 },
        data: { ...defaultNodeData[type] },
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [nodes.length, setNodes]
  );

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
    setShowPalette(false);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onNodeChange = useCallback(
    (updatedNode) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === updatedNode.id ? updatedNode : n))
      );
      setSelectedNode(updatedNode);
    },
    [setNodes]
  );

  const onDeleteNode = useCallback(
    (nodeId) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNode(null);
    },
    [setNodes, setEdges]
  );

  const handleSave = () => {
    if (onSave) onSave(nodes, edges);
  };

  return (
    <div className="flex h-full" ref={reactFlowWrapper}>
      {/* Left Sidebar: Palette or Config */}
      <div className="hidden md:flex w-[260px] flex-shrink-0 flex-col border-r border-surface-border bg-surface-card p-3 overflow-y-auto">
        {selectedNode ? (
          <AutomationNodeConfigPanel
            node={selectedNode}
            onChange={onNodeChange}
            onClose={() => setSelectedNode(null)}
            onDelete={onDeleteNode}
            sessions={sessions}
          />
        ) : (
          <AutomationNodePalette nodes={nodes} onAddNode={onAddNode} />
        )}
      </div>

      {/* Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onInit={setReactFlowInstance}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          fitView
          proOptions={{ hideAttribution: true }}
          deleteKeyCode={["Backspace", "Delete"]}
        >
          <Background variant="dots" gap={20} size={1} />
          <Controls position="bottom-left" />
          <MiniMap
            position="bottom-right"
            nodeColor={(n) => MINIMAP_COLORS[n.type] || "#94a3b8"}
            maskColor="rgba(0,0,0,0.08)"
          />
        </ReactFlow>

        {/* Save button */}
        <button
          onClick={handleSave}
          className="absolute top-3 right-3 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg shadow-md hover:bg-brand-700 transition-colors z-10"
        >
          Save Flow
        </button>

        {/* Mobile: toggle palette/config */}
        <button
          onClick={() => {
            if (selectedNode) setSelectedNode(null);
            else setShowPalette((v) => !v);
          }}
          className="md:hidden absolute bottom-4 right-4 w-12 h-12 bg-brand-600 text-white rounded-full shadow-lg flex items-center justify-center z-10 text-xl"
        >
          {selectedNode ? "×" : "+"}
        </button>

        {/* Mobile panel */}
        {(showPalette || selectedNode) && (
          <div className="md:hidden absolute bottom-20 left-2 right-2 bg-surface-card rounded-xl shadow-xl border border-surface-border p-3 max-h-[50vh] overflow-y-auto z-20">
            {selectedNode ? (
              <AutomationNodeConfigPanel
                node={selectedNode}
                onChange={onNodeChange}
                onClose={() => setSelectedNode(null)}
                onDelete={onDeleteNode}
                sessions={sessions}
              />
            ) : (
              <AutomationNodePalette nodes={nodes} onAddNode={(type) => { onAddNode(type); setShowPalette(false); }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
